import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  selectAircraftConnectionById,
  selectCommand,
} from "./aircraftSelectors";
import { arm, autoarm, bind, land, ping, rtl, smartRtl } from "./aircraftAPI";
import {
  AircraftEvent,
  Command,
  DirectTelemetry,
  errorHasMessage,
  IAircraft,
  isCriticalTelemetry,
  Role,
  Sim,
  Token,
  Uav,
  UavNotificationStamped,
} from "./types";
import jwtDecode from "jwt-decode";
import { localIp } from "../../constants";
import { AppThunk, RootState } from "../../app/store";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { Logger } from "../../tools/Logger";
import { v4 as uuidv4 } from "uuid";
import axios, { AxiosResponse } from "axios";
import { connectThunk, getAircraftListThunk } from "./aircraftActions";
import { setUavMission } from "../mission/missionSlice";

const ac = require("./sample.json");

const TAG = "aircraftSlice";
export interface AircraftState {
  value: number;
  hoverUav: string;
  selectedUav: string;
  command: Command | "";
  controlledUav: string;
  status: "idle" | "loading" | "failed";
  lastEvent: { [key: string]: AircraftEvent };
  live: { [key: string]: IAircraft };
  direct: { [key: string]: DirectTelemetry };
  connections: { [key: string]: Connection };
  notifications: {
    [key: string]: UavNotificationStamped[];
  };
  clientId: string;
  uavList: Uav[];
  landingPads: { [uavId: string]: { name: string; lat: number; lon: number } };
  uavGeofenceIds: { [uavId: string]: string };
}

export const bindThunk = createAsyncThunk<
  { data: Token; token: string; sims: Sim[]; uavId: string; port: string },
  { role?: Role; sims: Sim[]; uavId: string; port: string },
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("aircraft/bind", async ({ role, sims, uavId, port }, thunkApi) => {
  try {
    const attemptBind = async (sim: Sim) => {
      const res = await bind(role, sim.ip, port);
      const decoded = jwtDecode<Token>(res?.data?.token);
      return thunkApi.fulfillWithValue(
        { data: decoded, token: res?.data?.token, sims, uavId, port },
        {
          message: "Bound!",
        }
      );
    };

    try {
      return await attemptBind(sims[0]);
    } catch (error) {
      return await attemptBind(sims[1]);
    }
  } catch (err) {
    console.log(err);
    thunkApi.dispatch(
      setUavNotifications({
        uavId,
        notification: {
          msg: "Get Token Failed.",
          type: "TOKEN_FAILED",
          action: ["DISCONNECT"],
          level: 5,
          timestamp: Date.now(),
          info: true,
        },
      })
    );
    if (errorHasMessage(err)) {
      thunkApi.rejectWithValue(err, {
        message: "Token failed: " + err?.message,
      });
    } else {
      return thunkApi.rejectWithValue(err, {
        message: "Token failed",
      });
    }
    throw new Error(err.message);
  }
});

const issueCommand =
  (
    route: string,
    _: string,
    connection: Connection
  ): AppThunk<Promise<AxiosResponse<string, any>>> =>
  async () => {
    const token = connection.tokenString;
    const requests = connection.sims.map(
      (sim) => `https://${sim?.ip}${connection?.port}/${route}`
    );
    const id = uuidv4();
    return await Promise.any(
      requests.map(async (request) => {
        try {
          const res = await axios.put<string>(
            request,
            {},
            {
              headers: { Authorization: "Bearer " + token, id },
              timeout: 10000,
            }
          );
          console.log(res);
          return res;
        } catch (err) {
          console.error(err);
          throw err;
        }
      })
    );
  };

export const issueCommandThunk = createAsyncThunk<
  { uavId: string; command?: Command },
  { uavId: string; command?: Command },
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("aircraft/command", async ({ uavId, command }, thunkApi) => {
  const commandToUse = command ? command : selectCommand(thunkApi.getState());
  try {
    const connection = selectAircraftConnectionById(thunkApi.getState(), uavId);

    if (!connection) {
      throw new Error(`No connection details for ${uavId}.`);
    }

    if (commandToUse === "COMMAND_RTL") {
      await thunkApi.dispatch(issueCommand("mission/rtl", uavId, connection));
    } else if (commandToUse === "COMMAND_ARM") {
      await thunkApi.dispatch(issueCommand("mission/arm", uavId, connection));
    } else if (commandToUse === "COMMAND_SMARTRTL") {
      await thunkApi.dispatch(
        issueCommand("mission/smartrtl", uavId, connection)
      );
    } else if (commandToUse === "COMMAND_ABORT") {
      await thunkApi.dispatch(issueCommand("mission/abort", uavId, connection));
    } else if (commandToUse === "COMMAND_CANCEL") {
      await thunkApi.dispatch(
        issueCommand("mission/cancel", uavId, connection)
      );
    } else if (commandToUse === "COMMAND_DELIVER") {
      await thunkApi.dispatch(
        issueCommand("mission/deliver", uavId, connection)
      );
      thunkApi.dispatch(onEvent({ event: "DELIVERY_COMMAND_ENDED", uavId }));
    } else if (commandToUse === "COMMAND_PAUSE") {
      await thunkApi.dispatch(issueCommand("mission/pause", uavId, connection));
    } else if (commandToUse === "COMMAND_RESUME") {
      await thunkApi.dispatch(
        issueCommand("mission/resume", uavId, connection)
      );
    } else if (commandToUse === "COMMAND_LAND") {
      await thunkApi.dispatch(issueCommand("mission/land", uavId, connection));
    } else if (commandToUse === "COMMAND_AUTO_ARM") {
      try {
        await autoarm(connection.tokenString, connection.sims[0].ip);
      } catch (err) {
        await autoarm(connection.tokenString, connection.sims[1].ip);
        console.error(err);
      }
    } else if (commandToUse === "COMMAND_PARACHUTE") {
      await thunkApi.dispatch(
        issueCommand("mission/parachute", uavId, connection)
      );
    } else {
      throw new Error(`Invalid command ${commandToUse}.`);
    }

    return thunkApi.fulfillWithValue(
      { uavId, commandToUse },
      {
        message: `${commandToUse} issued.`,
      }
    );
  } catch (err) {
    if (errorHasMessage(err)) {
      return thunkApi.rejectWithValue(err, {
        message: `${commandToUse} to ${uavId} failed. ` + err?.message,
      });
    }
    return thunkApi.rejectWithValue(err, {
      message: `${commandToUse} to ${uavId} failed`,
    });
  }
});

export const pingThunk = createAsyncThunk<
  {},
  undefined,
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("aircraft/ping", async (arg, thunkApi) => {
  try {
    const res = await ping(localIp);
    return thunkApi.fulfillWithValue(res, {
      message: "Ping!",
    });
  } catch (err) {
    console.log(err);

    if (errorHasMessage(err)) {
      return thunkApi.rejectWithValue(err, {
        message: "Ping failed: " + err?.message,
      });
    }
    return thunkApi.rejectWithValue(err, {
      message: "Ping failed",
    });
  }
});

const mergeNewSimData = (
  socket: {
    index: number;
    status: string;
    active: boolean;
  },
  sims: Sim[]
) => {
  return sims.map((sim, i) => {
    if (i !== socket.index) return sim;
    return {
      ...sim,
      status: socket.status,
      active: socket.active,
    };
  });
};

export interface Connection {
  connected: boolean;
  connecting: boolean;
  token: Token;
  retries: number;
  tokenString: string;
  url?: string;
  sims: Sim[];
  uavId: string;
  stale?: number;
  abort?: number;
  port?: string;
}

const initialState: AircraftState = {
  value: 0,
  status: "idle",
  live: ac || {},
  direct: {},
  connections: {},
  lastEvent: {},
  hoverUav: "",
  selectedUav: "",
  controlledUav: "",
  command: "",
  notifications: {},
  clientId: uuidv4(),
  uavList: [],
  landingPads: {},
  uavGeofenceIds: {},
};

export const aircraftSlice = createSlice({
  name: "aircraft",
  initialState,
  reducers: {
    updateLiveAircraft: (
      state,
      action: PayloadAction<{ [key: string]: IAircraft }>
    ) => {
      state.live = action.payload;
    },
    tokenReceived: (state, action: PayloadAction<Token>) => {},
    setHoverUav: (state, action: PayloadAction<string>) => {
      state.hoverUav = action.payload;
    },
    setSelectedUav: (state, action: PayloadAction<string>) => {
      state.selectedUav = action.payload;
    },
    setControlledUav: (state, action: PayloadAction<string>) => {
      state.controlledUav = action.payload;
    },
    setCommand: (state, action: PayloadAction<Command | "">) => {
      state.command = action.payload;
    },
    resetConnections: (state, action: PayloadAction<undefined>) => {
      state.connections = {};
      state.notifications = {};
      state.direct = {};
    },
    tokenExpiring: (
      _,
      __: PayloadAction<{ uavId: string; expiresInMinutes: number }>
    ) => {},
    tokenExpired: (_, __: PayloadAction<{ uavId: string }>) => {},
    telemetryOld: (_, __: PayloadAction<{ uavId: string }>) => {},
    restartUavConnection: (state, action: PayloadAction<{ uavId: string }>) => {
      const { [action.payload.uavId]: removeConnections, ...restConnections } =
        state.connections;
      const { [action.payload.uavId]: removeDirect, ...restDirect } =
        state.direct;
      const { [action.payload.uavId]: removeNotifs, ...restNotifs } =
        state.notifications;
      state.connections = restConnections;
      state.direct = restDirect;
      state.notifications = restNotifs;
    },
    retryUavConnection: (state, action: PayloadAction<{ uavId: string }>) => {
      const { [action.payload.uavId]: remove, ...rest } = state.connections;

      state.connections = rest;
    },

    setUavNotifications: (
      state,
      {
        payload: { uavId, notification },
      }: PayloadAction<{
        uavId: string;
        notification: UavNotificationStamped;
      }>
    ) => {
      Logger.log("debug", TAG, `notification: ${notification?.msg}`);
      try {
        const uavNotifications = state.notifications[uavId];
        const typeExists = uavNotifications?.find(
          (notif) => notif.type === notification?.type
        );

        const newNotificationList = !typeExists
          ? [
              { ...notification, timestamp: Date.now() },
              ...(state.notifications[uavId] || []),
            ]
          : state.notifications[uavId]?.map((notif) => {
              if (notif?.type === notification?.type) {
                return {
                  ...notification,
                  timestamp: Date.now(),
                };
              } else {
                return notif;
              }
            });

        const sortedTimes = [...newNotificationList].sort(
          (a, b) => b?.timestamp - a?.timestamp
        );
        const filteredNotifications = sortedTimes.filter(
          (notification) => Date.now() - notification.timestamp < 60000
        );
        state.notifications[uavId] = filteredNotifications;
      } catch (err) {
        Logger.error("notification Middleware", err);
      }
    },
    setPadStatus: (
      state,
      action: PayloadAction<{
        uavId: string;
        name: string;
        lat: number;
        lon: number;
      }>
    ) => {
      state.landingPads[action.payload.uavId] = {
        name: action.payload.name,
        lat: action.payload.lat,
        lon: action.payload.lon,
      };
    },
    telemetryNotify: (
      state,
      action: PayloadAction<{
        uavId: string;
      }>
    ) => {
      if (action.payload.uavId && state.connections[action.payload.uavId]) {
        state.connections[action.payload.uavId] = {
          ...state.connections[action.payload.uavId],
          abort: Date.now(),
        };
      }
    },

    telemetryStale: (
      state,
      action: PayloadAction<{
        uavId: string;
      }>
    ) => {
      if (action.payload.uavId && state.connections[action.payload.uavId]) {
        state.connections[action.payload.uavId] = {
          ...state.connections[action.payload.uavId],
          stale: Date.now(),
        };
      }
    },
    aircraftConnecting: (
      state,
      action: PayloadAction<{
        uavId: string;
      }>
    ) => {
      if (action.payload.uavId && state.connections[action.payload.uavId]) {
        state.connections[action.payload.uavId] = {
          ...state.connections[action.payload.uavId],
          connecting: true,
        };
      }
    },
    telemetryAbort: (
      state,
      action: PayloadAction<{
        uavId: string;
      }>
    ) => {
      if (action.payload.uavId && state.connections[action.payload.uavId]) {
        state.connections[action.payload.uavId] = {
          ...state.connections[action.payload.uavId],
          abort: Date.now(),
        };
      }
    },
    setTelemetry: (
      state,
      action: PayloadAction<{ telemetry: DirectTelemetry; simIndex: 0 | 1 }>
    ) => {
      if (!action.payload.telemetry) return;
      const { payload: telemetryPayload } = action.payload.telemetry;
      try {
        if (
          telemetryPayload.uav_id &&
          state.connections?.[telemetryPayload.uav_id]
        ) {
          state.direct[telemetryPayload.uav_id] = {
            ...action.payload.telemetry,
            timestamp: Date.now(),
          };
        }
      } catch (err) {
        Logger.error(TAG, err);
      }

      try {
        if (
          telemetryPayload.uav_id &&
          state.connections[telemetryPayload.uav_id]
        ) {
          if (!isCriticalTelemetry(action.payload.telemetry.payload)) {
            state.connections[telemetryPayload.uav_id] = {
              ...state.connections[telemetryPayload.uav_id],
              abort: undefined,
              stale: undefined,
            };
          }
        }
      } catch (err) {
        Logger.error(TAG, err);
      }

      try {
        if (
          telemetryPayload.uav_id &&
          !isCriticalTelemetry(telemetryPayload) && 
          (telemetryPayload as any).geofence_id
        ) {
          state.uavGeofenceIds[telemetryPayload.uav_id] = (telemetryPayload as any).geofence_id;
        }
      } catch (err) {
        Logger.error(TAG, err);
      }
    },
    onPingStart: (
      state,
      action: PayloadAction<{ id: string; sims: Sim[] }>
    ) => {
      if (state.connections[action.payload.id]) {
        state.connections[action.payload.id] = {
          ...state.connections[action.payload.id],
          sims: action.payload.sims,
        };
      }
    },
    onPingFinish: (
      state,
      action: PayloadAction<{ id: string; ip: string; success: boolean }>
    ) => {
      if (state.connections[action.payload.id]) {
        const simData = state.connections[action.payload.id].sims.map((sim) => {
          if (sim.ip === action.payload.ip) {
            return {
              ...sim,
              available: action.payload.success,
            };
          }
          return sim;
        });
        state.connections[action.payload.id] = {
          ...state.connections[action.payload.id],
          sims: simData,
        };
      }
    },
    onOpen: (state, action: PayloadAction<{ id: string; ip: string }>) => {
      const connection = state.connections[action.payload.id];

      if (connection) {
        const simData = state.connections[action.payload.id].sims.map((sim) => {
          if (sim.ip === action.payload.ip) {
            return {
              ...sim,
              active: true,
              status: "ok",
            };
          }
          return sim;
        });
        state.connections[action.payload.id] = {
          ...state.connections[action.payload.id],
          connected: true,
          connecting: false,
          sims: simData,
        };
      }
    },
    onError: (
      state,
      action: PayloadAction<{
        id: string;
        socket: {
          index: number;
          status: string;
          active: boolean;
        };
      }>
    ) => {
      const connection = state.connections[action.payload.id];
      if (connection) {
        const newSimData = mergeNewSimData(
          action.payload.socket,
          connection.sims
        );
        state.connections[action.payload.id] = {
          ...state.connections[action.payload.id],
          connected: false,
          connecting: false,
          sims: newSimData,
        };
      }
    },
    onClose: (
      state,
      action: PayloadAction<{
        id: string;
        socket: {
          index: number;
          status: string;
          active: boolean;
        };
      }>
    ) => {
      const connection = state.connections[action.payload.id];
      if (connection) {
        const newSimData = mergeNewSimData(
          action.payload.socket,
          connection.sims
        );

        state.connections[action.payload.id] = {
          ...state.connections[action.payload.id],
          connected: newSimData.some((sim) => sim.active),
          connecting: false,
          sims: newSimData,
        };
      }
    },

    startSocket: (state, action: PayloadAction<{ id: string }>) => {
      if (state.connections[action.payload.id]) {
        state.connections[action.payload.id] = {
          ...state.connections[action.payload.id],
          connecting: true,
        };
      }
    },
    onEvent: (
      state,
      action: PayloadAction<{ event: AircraftEvent; uavId: string }>
    ) => {
      if (action.payload.event === "DELIVERY_COMMAND") {
        if (
          !["DELIVERY_COMMAND_ENDED", "DOORS_OPEN"].includes(
            state.lastEvent[action.payload.uavId]
          )
        ) {
          state.lastEvent[action.payload.uavId] = action.payload.event;
        }
      } else {
        state.lastEvent[action.payload.uavId] = action.payload.event;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(bindThunk.fulfilled, (state, action) => {
      const miniId = action.payload.data.iss.replace("MNA-", "");
      if (!state.connections[miniId]) {
        state.connections[miniId] = {
          connected: false,
          connecting: false,
          token: action.payload.data,
          tokenString: action.payload.token,
          retries: 0,
          sims: [],
          uavId: miniId,
          port: action.payload.port,
        };
      }
    });
    builder.addCase(connectThunk.fulfilled, (_, __) => {});

    builder.addCase(setUavMission, (state, action) => {
      const currentEvent = state.lastEvent[action.payload.uavId];
      if (
        ![
          "MISSION_STARTED",
          "DOORS_OPEN",
          "MISSION_IN_FLIGHT",
          "DELIVERY_COMMAND",
          "AT_DROP",
          "DOORS_CLOSED",
          "DELIVERY_STARTED",
        ].includes(currentEvent)
      ) {
        state.lastEvent[action.payload.uavId] = "MISSION_RECEIVED";
      }
    });

    builder.addCase(issueCommandThunk.fulfilled, (state, action) => {
      if (action.meta.arg.command === "COMMAND_CANCEL") {
        if (state.lastEvent[action.payload.uavId]) {
          const { [action.meta.arg.uavId]: remove, ...rest } = state.lastEvent;
          state.lastEvent = rest;
        }
      }
    });
    builder.addCase(getAircraftListThunk.fulfilled, (state, action) => {
      state.uavList = action.payload;
    });
  },
});

export const {
  updateLiveAircraft,
  setHoverUav,
  setSelectedUav,
  setControlledUav,
  setCommand,
  setTelemetry,
  onOpen,
  onError,
  onClose,
  startSocket,
  onPingStart,
  onPingFinish,
  telemetryNotify,
  telemetryStale,
  telemetryAbort,
  setUavNotifications,
  setPadStatus,
  restartUavConnection,
  tokenExpiring,
  tokenExpired,
  telemetryOld,
  resetConnections,
  aircraftConnecting,
  onEvent,
} = aircraftSlice.actions;

export default persistReducer(
  {
    key: "root",
    storage,
    whitelist: ["live", "direct", "connections", "clientId", "lastEvent", "landingPads"],
  },
  aircraftSlice.reducer
);
